import { useCallback, useState } from 'react'
import { Toaster } from 'src/components/atoms'
import { handleError } from 'src/fixtures/utils/error'
import useSWR, { useSWRConfig } from 'swr'
import { useAuthenticate } from '../auth/hooks'
import { SWRCachePath } from '../swr-cach-path'
import { useListDocumentFilter } from '../documents/hooks'
import { DocumentFolder } from '@noco/http-client/lib/noco'
import { nocoSDK } from 'src/fixtures/modules/initialize'
import { useGetCurrentCacheKeys } from '../utils/hooks'

/**
 * フォルダ一覧取得
 */
export const useListDocumentFolders = () => {
  const { data: auth } = useAuthenticate()
  const folderApi = nocoSDK.client?.userService.documentFolderApi

  const func = useCallback(async (): Promise<{ documentFolders: DocumentFolder[] } | undefined> => {
    if (!auth) return undefined
    const res = await folderApi?.ApiFactory(auth.token).userV1DocumentFoldersGet()
    return { documentFolders: res?.data?.documentFolders ?? [] }
  }, [auth, folderApi])

  return useSWR(auth?.token ? SWRCachePath.listDocumentFolders() : null, func, {
    onError: err => handleError(err, { tag: 'List document folders' })
  })
}

/**
 * フォルダ作成
 */
export const useCreateDocumentFolder = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate: mutateListFolders } = useListDocumentFolders()
  const folderApi = nocoSDK.client?.userService.documentFolderApi

  const handleCreateFolder = useCallback(
    async (request: { name: string }): Promise<DocumentFolder | undefined> => {
      try {
        if (!auth) return undefined
        setError(undefined)
        setIsLoading(true)

        const res = await folderApi?.ApiFactory(auth.token).userV1DocumentFoldersPost({
          createDocumentFolderRequest: {
            documentFolder: { name: request.name }
          }
        })

        await mutateListFolders()
        Toaster.success('フォルダを作成しました')

        return res?.data?.documentFolder
      } catch (err) {
        await handleError(err, { setError, tag: 'Create document folder' })
        return undefined
      } finally {
        setIsLoading(false)
      }
    },
    [auth, folderApi, mutateListFolders]
  )

  return { error, isLoading, handleCreateFolder }
}

/**
 * フォルダ更新
 */
export const useUpdateDocumentFolder = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate: mutateListFolders } = useListDocumentFolders()
  const folderApi = nocoSDK.client?.userService.documentFolderApi

  const handleUpdateFolder = useCallback(
    async (folderId: string, request: { name: string }): Promise<boolean> => {
      try {
        if (!auth) return false
        setError(undefined)
        setIsLoading(true)

        await folderApi?.ApiFactory(auth.token).userV1DocumentFoldersDocumentFolderIdPut({
          documentFolderId: folderId,
          updateDocumentFolderRequest: {
            documentFolder: { name: request.name }
          }
        })

        await mutateListFolders()
        Toaster.success('フォルダ名を変更しました')
        return true
      } catch (err) {
        await handleError(err, { setError, tag: 'Update document folder' })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [auth, folderApi, mutateListFolders]
  )

  return { error, isLoading, handleUpdateFolder }
}

/**
 * フォルダ削除
 */
export const useDeleteDocumentFolder = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate: mutateListFolders } = useListDocumentFolders()
  const folderApi = nocoSDK.client?.userService.documentFolderApi

  const handleDeleteFolder = useCallback(
    async (folderId: string, documentCount: number): Promise<boolean> => {
      try {
        if (!auth) return false

        // フォルダ内に資料がある場合は削除不可
        if (documentCount > 0) {
          Toaster.error(`このフォルダには${documentCount}件の資料が含まれているため削除できません`)
          return false
        }

        setError(undefined)
        setIsLoading(true)

        await folderApi?.ApiFactory(auth.token).userV1DocumentFoldersDocumentFolderIdDelete({
          documentFolderId: folderId
        })

        await mutateListFolders()
        Toaster.success('フォルダを削除しました')
        return true
      } catch (err) {
        await handleError(err, { setError, tag: 'Delete document folder' })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [auth, folderApi, mutateListFolders]
  )

  return { error, isLoading, handleDeleteFolder }
}

/**
 * 資料をフォルダに移動
 */
export const useMoveDocumentsToFolder = () => {
  const { data: auth } = useAuthenticate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const { mutate: globalMutate } = useSWRConfig()
  const listDocumentsCacheKeys = useGetCurrentCacheKeys('listDocuments')
  const { mutate: mutateDocumentsFilter } = useListDocumentFilter()
  const { mutate: mutateListFolders } = useListDocumentFolders()
  const documentApi = nocoSDK.client?.userService.documentApi

  // すべてのlistDocumentsキャッシュを再取得
  const revalidateAllListDocuments = useCallback(() => {
    const mutations = listDocumentsCacheKeys.map(key => globalMutate(key, undefined, { revalidate: true }))
    return Promise.all(mutations)
  }, [listDocumentsCacheKeys, globalMutate])

  const handleMoveDocuments = useCallback(
    async (request: { documentIds: string[]; documentFolderId: string }): Promise<boolean> => {
      try {
        if (!auth) return false
        setError(undefined)
        setIsLoading(true)

        await documentApi?.ApiFactory(auth.token).userV1DocumentsBulkMoveFolderPut({
          bulkMoveFolderDocumentsRequest: {
            ids: request.documentIds,
            folderId: request.documentFolderId
          }
        })

        await Promise.all([revalidateAllListDocuments(), mutateDocumentsFilter(), mutateListFolders()])

        const count = request.documentIds.length
        Toaster.success(`${count}件の資料をフォルダに移動しました`)
        return true
      } catch (err) {
        await handleError(err, { setError, tag: 'Move documents to folder' })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [auth, documentApi, revalidateAllListDocuments, mutateDocumentsFilter, mutateListFolders]
  )

  const handleRemoveFromFolder = useCallback(
    async (documentIds: string[]): Promise<boolean> => {
      try {
        if (!auth) return false
        setError(undefined)
        setIsLoading(true)

        await documentApi?.ApiFactory(auth.token).userV1DocumentsBulkRemoveFolderPut({
          bulkRemoveFolderDocumentsRequest: {
            ids: documentIds
          }
        })

        await Promise.all([revalidateAllListDocuments(), mutateDocumentsFilter(), mutateListFolders()])

        const count = documentIds.length
        Toaster.success(`${count}件の資料をフォルダから解除しました`)
        return true
      } catch (err) {
        await handleError(err, { setError, tag: 'Remove documents from folder' })
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [auth, documentApi, revalidateAllListDocuments, mutateDocumentsFilter, mutateListFolders]
  )

  return { error, isLoading, handleMoveDocuments, handleRemoveFromFolder }
}
